import React, { Fragment, useState, useEffect, useRef } from 'react'
import { useTable, useSortBy, useGlobalFilter, useAsyncDebounce, usePagination, useExpanded } from 'react-table'
import { useExportData } from 'react-table-plugins'
import { useMediaQuery } from 'react-responsive'
import Papa from "papaparse";
import * as XLSX from "xlsx";
import JsPDF from "jspdf";
import "jspdf-autotable";
import Utilities from '../../../services/commonservice/utilities';
const Table2 = (props) => {
    const isMobile = useMediaQuery({ query: `(max-width: 576px)` });
    const [expanded, setExpanded] = useState({}); // new state to track expanded rows
    // Define sort functions
    const sortFunctions = {
        caseInsensitive: (rowA, rowB, columnId) => {
            const a = rowA.values[columnId] || '';
            const b = rowB.values[columnId] || '';
            return a.toString().toLowerCase().localeCompare(b.toString().toLowerCase());
        },
        numeric: (rowA, rowB, columnId) => {
            const a = parseFloat(rowA. values[columnId]) || 0;
            const b = parseFloat(rowB.values[columnId]) || 0;
            return a - b;
        },
        date: (rowA, rowB, columnId) => {
            const a = new Date(rowA.values[columnId]);
            const b = new Date(rowB.values[columnId]);
            return a - b;
        }
    };

    const data = React.useMemo(
        () => {
            if (Array.isArray(props.data)) { return [...props.data] } else {
                return ([])
            }
        },
        [props.data]
    )

    const hiddenColumns = React.useMemo(() => {
        if (Array.isArray(props.hiddenColumns) && isMobile)
            return props.hiddenColumns
        else if (Array.isArray(props.desktopHiddenColumns) && !isMobile) {
            return props.desktopHiddenColumns
        }
        else {
            return []
        }
    }, [props.hiddenColumns])

    const columns = React.useMemo(
        () => [...props.columns],
        [props.columns]
    )

    const [filter, setFilter] = React.useState("")
    const handleRowClick = (row) => {
        setExpanded((prevExpanded) => {
            const newExpanded = { ...prevExpanded };
            if (props.allowMultipleExpanded) {
                // Just toggle this row's expansion
                newExpanded[row.id] = !prevExpanded[row.id];
            } else {
                // Collapse all others and toggle only this one
                Object.keys(newExpanded).forEach((id) => {
                    if (id !== row.id) {
                        newExpanded[id] = false;
                    }
                });
                newExpanded[row.id] = !newExpanded[row.id] ?? true;
            }
            return newExpanded;
        });
    };

    function GlobalFilter({
        preGlobalFilteredRows,
        setGlobalFilter,
        setFilter,
        filter
    }) {
        const count = preGlobalFilteredRows.length
        const [value, setValue] = React.useState(filter)
        const onChange = useAsyncDebounce(value => {
            setFilter(value);
            setGlobalFilter(value || undefined);
        }, 500)

        const inputReference = useRef(null);

        useEffect(() => {
            inputReference.current.focus();
        });

        return (
            <span>
                Search:{' '}
                <input
                    type="text"
                    value={value || ""}
                    onChange={e => {
                        setValue(e.target.value);
                        onChange(e.target.value);
                    }}
                    placeholder={props.loading ? 'loading...' : `${count} records...`}
                    ref={inputReference}
                />
            </span>
        )
    }
    function generatePdfDocument({ safeFileName, headerNames, exportRows, columns }) {
        // Columns to sum
        const sumColIdx = columns
            .map((col, idx) => (col?.meta?.sum ? idx : -1))
            .filter(i => i >= 0);

        // Numeric columns (right-align header + body)
        const numericColIdx = columns
            .map((col, idx) => (col?.meta?.numeric ? idx : -1))
            .filter(i => i >= 0);

        // ColumnStyles for summed columns
        const columnStyles = {};
        sumColIdx.forEach(i => (columnStyles[i] = { halign: "right" }));

        const doc = new JsPDF("l", "pt", "letter");
        doc.text(safeFileName, 15, 20);

        doc.autoTable({
            head: [headerNames],
            body: sumColIdx.length > 0 ? exportRows.slice(0, -1) : exportRows,
            foot: sumColIdx.length > 0 ? [exportRows[exportRows.length - 1]] : [],
            margin: { top: 30 },

            headStyles: { fontSize: 9, fontStyle: "bold", valign: "top" },
            bodyStyles: { fontSize: 9 },
            footStyles: { fontSize: 9, fontStyle: "bold" },
            styles: { minCellHeight: 9, 
                halign: "left", 
                valign: "center" ,
                overflow: "linebreak",   // allow wrapping inside cells
                valign: "top" 
            },

            columnStyles,

            didParseCell: (data) => {
                const col = data.column.index;

                // Right-align numeric columns
                if ((data.section === "head" || data.section === "body") && numericColIdx.includes(col)) {
                    data.cell.styles.halign = "right";
                }

                // Right-align summed columns in footer, blank non-sum cells
                if (data.section === "foot") {
                    if (sumColIdx.includes(col)) {
                        data.cell.styles.halign = "right";
                    } else if (col !== 0) {
                        data.cell.text = [""]; // blank non-sum, non-label cells
                    }
                }
            }
        });

        return doc;
    }
    function prepareExportData(data, columns) {
        const toNumber = (v) => {
            if (v == null) return 0;
            if (typeof v === "number") return v;
            const s = String(v).trim();
            const cleaned = s.replace(/[^\d.\-()]/g, "");
            const val = cleaned.includes("(") && cleaned.includes(")")
                ? -parseFloat(cleaned.replace(/[()]/g, ""))
                : parseFloat(cleaned.replace(/,/g, ""));
            return Number.isFinite(val) ? val : 0;
        };
        
        let exportData = [...data];

        // Add subtotals if configured
        if (props.subtotalGroups && Array.isArray(props.subtotalGroups)) {
            const subtotalRows = calculateSubtotalsForOriginalData(props.data, columns);
            exportData = [...exportData, ...subtotalRows];
        }

        // Determine columns to sum
        const sumColIdx = columns.map((col, idx) => col?.meta?.sum ? idx : -1).filter(i => i >= 0);

        // Only add totals row if there are columns to sum
        if (sumColIdx.length > 0) {
            const totalsRow = columns.map((col, colIdx) => {
                if (sumColIdx.includes(colIdx)) {
                    const total = data.reduce((acc, row) => acc + toNumber(row[colIdx]), 0);
                    if (col?.meta?.currency && typeof Utilities?.toDollar === "function") {
                        return Utilities.toDollar(total);
                    }
                    if (col?.meta?.currency) {
                        return total % 1 === 0 ? `${total}.00` : total.toFixed(2);
                    } else {
                        return total % 1 === 0 ? total : total.toFixed(2);
                    }
                }
                return colIdx === 0 ? "Totals" : "";
            });

            exportData.push(totalsRow);
        }

        return exportData;
    }

    function getExportFileBlob({ columns, data, fileType, fileName }) {
        const safeFileName = fileName ? String(fileName) : "Report";
    
    // Filter out columns that should be excluded from export
    const exportColumns = columns.filter(col => !col.excludeFromExport);
    
    // Get header names - use exportValue if present, otherwise fall back to Header or id
    const headerNames = exportColumns.map(c => {
        // Priority 1: Use exportValue if present
        if (c.exportValue) {
            return c.exportValue;
        }
        // Priority 2: Fall back to Header
        else if (c.Header) {
            return c.Header;
        }
        // Priority 3: Fall back to id
        else if (c.id) {
            return c.id;
        }
        // Priority 4: Default fallback
        return 'Column';
    });
    
    const exportRows = prepareExportData(data, exportColumns);

        if (fileType === "csv") {
            const csvString = Papa.unparse({ fields: headerNames, data: exportRows });
            return new Blob([csvString], { type: "text/csv" });
        }

        if (fileType === "XLSX") {
            const jsonData = exportRows.map(row => {
                const obj = {};
                headerNames.forEach((col, idx) => {
                    obj[col] = row[idx];
                });
                return obj;
            });
            let wb = XLSX.utils.book_new();
            let ws = XLSX.utils.json_to_sheet(jsonData, { header: headerNames });
            XLSX.utils.book_append_sheet(wb, ws, "React Table Data");
            XLSX.writeFile(wb, `${safeFileName}.XLSX`);
            return false;
        }

        if (fileType === "pdf") {
        const doc = generatePdfDocument({ 
            safeFileName, 
            headerNames, 
            exportRows, 
            columns: exportColumns // Use filtered exportColumns for PDF
        });
            doc.save(`${safeFileName}.pdf`);
            return false;
        }

        if (fileType === "print") {
        const doc = generatePdfDocument({ 
            safeFileName, 
            headerNames, 
            exportRows, 
            columns: exportColumns // Use filtered exportColumns for PDF
        });
            const pdfBlobUrl = doc.output("bloburl");
            const printWindow = window.open(pdfBlobUrl);
            printWindow.addEventListener("load", () => {
                printWindow.print();
            });
            return false;
        }

        return false;
    }


    // Helper function to calculate subtotals for original data
    function calculateSubtotalsForOriginalData(originalData, columns) {
        if (!props.subtotalGroups || !Array.isArray(props.subtotalGroups)) {
            return [];
        }

        const subtotalRows = [];

        props.subtotalGroups.forEach(group => {
            const {
                groupField,
                groupValue,
                label,
                sumColumns = []
            } = group;

            // Filter original data for this group
            const groupData = originalData.filter(item => item[groupField] === groupValue);

            // Create subtotal row array
            const subtotalRow = new Array(columns.length).fill('');
            subtotalRow[0] = label; // Put label in first column

            sumColumns.forEach(colId => {
                const colIndex = columns.findIndex(col => col.id === colId);
                if (colIndex !== -1) {
                    const sum = groupData.reduce((acc, item) => {
                        return acc + (item[colId] || 0);
                    }, 0);

                    const column = columns[colIndex];
                    if (column?.meta?.currency && typeof Utilities?.toDollar === "function") {
                        subtotalRow[colIndex] = Utilities.toDollar(sum);
                    } else {
                        subtotalRow[colIndex] = sum.toFixed(2);
                    }
                }
            });

            subtotalRows.push(subtotalRow);
        });

        return subtotalRows;
    }

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        footerGroups,
        preGlobalFilteredRows,
        setGlobalFilter,
        globalFilter,
        rows,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize, expanded: expandedState },
        prepareRow,
        exportData,
    } = useTable({
        columns,
        data,
        initialState: { pageIndex: 0, hiddenColumns: hiddenColumns },
        getExportFileBlob,
        getExportFileName: ({ all }) => { return props.title },
        subDataAccessor: ({ all }) => { return props.subDataAccessor },
        // Add custom sort types here
        sortTypes: sortFunctions,
        defaultColumn: {
            sortType: props.defaultSortType || 'caseInsensitive'
        }
    },
        useGlobalFilter,
        useSortBy,
        useExpanded,
        usePagination,
        useExportData
    );

    return (
        <div className="table-2-template">
            {props.extraElements || props.extraButtons || props.searchable || props.export ? <div className={`row d-flex justify-content-end ${props.hidden ? '' : 'mb-3'} align-items-end g-3 mx-0 px-0`}>
                {props.extraElements?.length > 0 ? (props.extraElements?.length > 1 ? <>{props.extraElements.map(element => element.children)}</> : <div className="col-md-auto col-12 extra-elements">{props.extraElements.map(element => element.children)}</div>) : null}
                {props.searchable && <div className='col-md-auto'>
                    <GlobalFilter
                        preGlobalFilteredRows={preGlobalFilteredRows}
                        setGlobalFilter={setGlobalFilter}
                        setFilter={setFilter}
                        filter={filter}

                    />
                </div>}
                <div className='col-md-auto'>
                    <div className='btn-group m-w-100'>
                        {props.export && <> <button
                            title="Download to PDF"
                            className='btn btn-primary'
                            disabled={!Array.isArray(props.data)}
                            onClick={(e) => {
                                e.preventDefault();
                                exportData("pdf", false);
                            }}
                        >
                            <i className="icon file pdf" />
                        </button>
                            <button
                                title="Download to Excel"
                                className='btn btn-primary'
                                disabled={!Array.isArray(props.data)}
                                onClick={(e) => {
                                    e.preventDefault();
                                    exportData("XLSX", false);
                                }}
                            >
                                <i className="icon file excel" />
                            </button>
                            <button
                                title="Download to CSV"
                                className='btn btn-primary'
                                disabled={!Array.isArray(props.data)}
                                onClick={(e) => {
                                    e.preventDefault();
                                    exportData("csv", false);
                                }}
                            >
                                <i className="icon download" />
                            </button>
                            <button
                                title="Print"
                                className='btn btn-primary'
                                disabled={!Array.isArray(props.data)}
                                onClick={(e) => {
                                    e.preventDefault();
                                    exportData("print", false);
                                }}
                            >
                                <i className="icon print" />
                            </button>
                        </>}
                        {props.extraButtons && props.extraButtons.map(button => {
                            return (<button title={button.title} className={`${button.className}`} onClick={e => { e.preventDefault(); if (button.onClick) { button.onClick() } }}>{button.children}</button>)
                        })}
                    </div>
                </div>
            </div> : null}
            <div style={{ overflowX: "auto" }} hidden={props.hidden}>
                <table {...getTableProps()} className="table table-borderless">
                    {headerGroups.map(headerGroup => (
                        <colgroup>
                            {headerGroup.headers.map(column => (
                                <col span={1} style={{ minWidth: column.minWidth }} />
                            ))}
                        </colgroup>)
                    )}
                    <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th
                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                        style={{ textAlign: column.textAlign }}
                                        className={column.disableSortBy ? '' : 'point'}
                                    >
                                        {column.render('Header')}
                                        {/* Add a sort direction indicator */}
                                        <span>
                                            {column.isSorted
                                                ? column.isSortedDesc
                                                    ? <i className="icon angle down" />
                                                    : <i className="icon angle up" />
                                                : ''}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page?.length > 0 ? page.map(row => {
                            prepareRow(row)
                            return (
                                <Fragment>
                                    <tr
                                        {...row.getRowProps()}
                                        // className={`${props.subDataAccessor && row.cells.length>0 ? "point" : ""} ${row.cells.depth > 0 ? "bg-light" : ""
                                        //     }`}
                                        onClick={(e) => { e.preventDefault(); if (props.subDataAccessor && row.cells.length) { handleRowClick(row) } }}
                                    >
                                        {row.cells.map((cell) => (

                                            <td
                                                className={`${props.subDataAccessor && row.cells.length > 0 ? "point" : ""} ${expanded[row.id] ? "bg-light" : ""} ${cell.column.id === props.subDataId ? "text-center" : ""
                                                    }`}
                                                {...cell.getCellProps()} >{cell.column.id === props.subDataId ? (props.subDataAccessor && row.cells.length > 0 && expanded[row.id] ? <i className='icon minus' style={{ fontSize: '0.8em' }} /> : <i className='icon plus' style={{ fontSize: '0.8em' }} />) : cell.render("Cell")}</td>
                                        ))}
                                    </tr>
                                    {/* {row.isExpanded &&
                                    <tr><td colSpan={row.cells.length}>{row.original.subData}</td></tr>} */}
                                    {expanded[row.id] && (
                                        <tr>
                                            {props.subDataAccessor &&
                                                <td colSpan={row.cells.length}>
                                                    {props.subDataAccessor(row.original)}
                                                </td>
                                            }
                                        </tr>
                                    )}
                                </Fragment>
                            )
                        }) :

                            props.loading ? <tr><td colSpan={columns.length}>Please be patient while data loads...</td></tr> :
                                props.initialMessage ? <tr><td colSpan={columns.length}>Please submit the report...</td></tr> :
                                    <tr><td colSpan={columns.length}>No Results Found</td></tr>}
                    </tbody>
                    {props.showFooter === false ? null :
                        (!props.loading && rows?.length > 0 ? (
                            <tfoot>
                                {footerGroups.map(group => (
                                    <tr className="thead" {...group.getFooterGroupProps()}>
                                        {group.headers.map(column => (

                                            <th {...column.getFooterProps(
                                                {
                                                    style: {
                                                        // width: column.column.width,
                                                        textAlign: column.textAlign,
                                                        // verticalAlign: 'middle'
                                                    }
                                                }
                                            )}
                                            >
                                                {column.render('Footer')}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </tfoot>) : null
                        )}
                </table>
            </div>
            {rows?.length > 10 && !props.noPagination && !props.hidden ?
                <div className='row d-flex align-items-center justify-content-between mt-3 pe-0 me-0'>
                    <div className='col'>
                        <div className="pagination align-items-center row d-flex justify-content-start">
                            <div className='btn-group col-md-auto col-12'>
                                <button className='btn btn-primary' onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                                    {'<<'}
                                </button>{' '}
                                <button className='btn btn-primary' onClick={() => previousPage()} disabled={!canPreviousPage}>
                                    {'<'}
                                </button>{' '}
                                <button className='btn btn-primary' onClick={() => nextPage()} disabled={!canNextPage}>
                                    {'>'}
                                </button>{' '}
                                <button className='btn btn-primary' onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                                    {'>>'}
                                </button>{' '}
                            </div>
                            <span className='col-md-auto col-12'>
                                Page{' '}
                                <strong>
                                    {pageIndex + 1} of {pageOptions.length}
                                </strong>{' '}
                            </span>
                        </div>
                    </div>
                    <div className='col-md-auto col-12'>
                        <strong>{page.length} of {rows.length} records</strong>
                    </div>
                </div> : null}
        </div>
    )
}

export default Table2